import { AfterViewInit, Component, OnDestroy, OnInit } from '@angular/core';
import { NavigationEnd, NavigationError, Router } from '@angular/router';
import { combineLatest, filter, startWith, Subject, takeUntil, tap } from 'rxjs';
import { Ga4Service } from './services/ga4/ga4.service';
import { DataService } from './services/data/data.service';
import { RoleCleanerService } from './services/roleCleaner/role-cleaner.service';
import { IdleTrackerService } from './services/idle-tracker/idle-tracker.service';
import { AuthDataService } from './services/auth/auth-data.service';
import { RELOAD_ATTEMPT_KEY, RELOAD_COOLDOWN_MS } from './utils/constants';
import { LIFERAY_WEB_CONSTANT } from './utils/liferay.constant';
import { LiferayService } from './services/liferay/liferay.service';
import { environment } from '../environments/environment';
import { IFeatureFlagsPerEnvironment } from './interfaces/IFeatureFlags';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  standalone: false,
  styleUrl: './app.component.scss',
})
export class AppComponent implements OnInit, OnDestroy, AfterViewInit {
  cleaner$: Subject<void> = new Subject();
  private keydownListener?: (e: KeyboardEvent) => void;
  private mousedownListener?: (e: MouseEvent) => void;
  constructor(
    private router: Router,
    private ga4: Ga4Service,
    private dataService: DataService,
    private roleCleaner: RoleCleanerService,
    private idleTracker: IdleTrackerService,
    private authData: AuthDataService,
    private liferayService: LiferayService
  ) { }

  ngOnInit(): void {
    this.idleTracker.startTracking();
    this.getFeatureFlags();
    // Clear any stale reload attempt flags on app initialization
    // This ensures the flag doesn't persist across successful page loads
    try {
      const lastReloadAttempt = sessionStorage.getItem(RELOAD_ATTEMPT_KEY);
      if (lastReloadAttempt) {
        const parsedTimestamp = parseInt(lastReloadAttempt, 10);
        // Validate that parseInt returned a valid number
        if (!isNaN(parsedTimestamp) && parsedTimestamp > 0) {
          const timeSinceLastAttempt = Date.now() - parsedTimestamp;
          // If more than cooldown time has passed, clear the flag (successful load)
          if (timeSinceLastAttempt > RELOAD_COOLDOWN_MS) {
            try {
              sessionStorage.removeItem(RELOAD_ATTEMPT_KEY);
            } catch (error) {
              // Ignore storage errors when clearing stale flags
              console.warn('Failed to clear reload attempt flag:', error);
            }
          }
        } else {
          // Invalid timestamp stored, clear it
          try {
            sessionStorage.removeItem(RELOAD_ATTEMPT_KEY);
          } catch (error) {
            console.warn('Failed to remove invalid reload attempt flag:', error);
          }
        }
      }
    } catch (error) {
      // sessionStorage might not be available (private browsing, quota exceeded, etc.)
      console.warn('Failed to access sessionStorage during initialization:', error);
    }
    
    this.keydownListener = (e: KeyboardEvent) => {
      if (e.key === "Tab") {
        document.body.classList.add("user-is-tabbing");
      }
    };

    window.addEventListener("keydown", this.keydownListener);
    this.mousedownListener = () => {
      document.body.classList.remove("user-is-tabbing");
    };
    window.addEventListener("mousedown", this.mousedownListener);
    this.initializeRouterEvents();

    this.router.events
      .pipe(
        filter((e): e is NavigationError => e instanceof NavigationError),
        takeUntil(this.cleaner$),
      )
      .subscribe(event => {
        // Extract error message from various possible error formats
        const errorMessage = event.error?.message || 
                            event.error?.toString() || 
                            (typeof event.error === 'string' ? event.error : '');
        
        // Check if it's a dynamic import/chunk loading error using regex for flexible matching
        // This catches variations like "Failed to fetch dynamically imported module", 
        // "ChunkLoadError", "Loading chunk 123 failed", "Failed to load module script", etc.
        const isChunkLoadError = errorMessage && typeof errorMessage === 'string' &&
          (/Failed to fetch dynamically imported module|ChunkLoadError|Loading chunk|Failed to load module script|Loading CSS chunk|chunk.*failed/i.test(errorMessage));
        
        if (event.error && isChunkLoadError) {
            // Handle the error: reload the page to recover from dynamic import failure
            console.error('Dynamic import error:', errorMessage, ' page:', this.router.url);
            
            // Prevent infinite reload loop by checking if we've recently attempted a reload
            // IMPORTANT: Always read fresh from sessionStorage here, not from outer scope
            // This ensures we get the current timestamp, not a stale value from initialization
            let shouldPreventReload = false;
            const now = Date.now();
            
            try {
              // Read current value from sessionStorage on each error (not from outer scope)
              const lastReloadAttempt = sessionStorage.getItem(RELOAD_ATTEMPT_KEY);
              if (lastReloadAttempt) {
                const parsedTimestamp = parseInt(lastReloadAttempt, 10);
                // Validate that parseInt returned a valid number
                if (!isNaN(parsedTimestamp) && parsedTimestamp > 0) {
                  const timeSinceLastAttempt = now - parsedTimestamp;
                  if (timeSinceLastAttempt < RELOAD_COOLDOWN_MS && timeSinceLastAttempt >= 0) {
                    // Too soon to reload again - prevent infinite loop
                    shouldPreventReload = true;
                  }
                }
              }
            } catch (error) {
              // sessionStorage might not be available (private browsing, quota exceeded, etc.)
              // In this case, we'll allow the reload but log a warning
              console.warn('Failed to check reload attempt from sessionStorage:', error);
            }
            
            if (shouldPreventReload) {
              console.error('Dynamic import error occurred again too soon after last reload attempt. Preventing infinite loop.');
              return;
            }
            
            // Store the reload attempt timestamp
            try {
              sessionStorage.setItem(RELOAD_ATTEMPT_KEY, now.toString());
            } catch (error) {
              // If storage fails, log warning but still proceed with reload
              // This prevents the reload from being blocked if storage quota is exceeded
              console.warn('Failed to store reload attempt timestamp:', error);
            }
            // Reload the page to recover from the dynamic import error
            console.warn('Reloading page to recover from dynamic import error...');
            window.location.reload();
          }
      });
  }

  ngAfterViewInit(): void {
    this.roleCleaner.init();
  }

  /**
   * @description Initialize router events subscription
   */
  private initializeRouterEvents(): void {
    const navigationEnd$ = this.router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      startWith(new NavigationEnd(
        0,
        this.router.url,
        this.router.url
      ))
    );

    // Strip sensitive access_token from URL in address bar (similar to VIVR flow)
    navigationEnd$.pipe(
      takeUntil(this.cleaner$),
      tap((event) => {
        const currentUrl = event.urlAfterRedirects || this.router.url;
        if (currentUrl.includes('access_token') || window.location.hash.includes('access_token')) {
          const urlTree = this.router.parseUrl(currentUrl);
          let hasSensitiveParams = false;

          if (urlTree.fragment?.includes('access_token')) {
            urlTree.fragment = null;
            hasSensitiveParams = true;
          }

          if (urlTree.queryParams['access_token']) {
            delete urlTree.queryParams['access_token'];
            hasSensitiveParams = true;
          }

          if (hasSensitiveParams) {
            this.router.navigateByUrl(urlTree, { replaceUrl: true });
          }

          if (window.location.hash.includes('access_token')) {
            window.history.replaceState(window.history.state, '', window.location.pathname + (window.location.search || ''));
          }
        }
      })
    ).subscribe();

    combineLatest([
      navigationEnd$,
      this.dataService.contractData$
    ]).pipe(
      takeUntil(this.cleaner$),
      filter(([event, contractData]) => {
        if (!contractData) {
          return false;
        }
        const urlTree = this.router.parseUrl(event.urlAfterRedirects || '');
        const hasAccessToken = !!(
          urlTree.fragment?.includes('access_token') ||
          urlTree.queryParams['access_token']
        );
        return !hasAccessToken;
      }),
      tap(([event, _]) => {
        const rawUrl = event.urlAfterRedirects || window.location.pathname;
        const pathWithoutParams = new URL(rawUrl, window.location.origin).pathname;
        const lastSegment = pathWithoutParams.split('/').filter(Boolean).pop() || '';
        this.ga4.sendPageView(lastSegment);
      })
    ).subscribe({
      error: (error) => console.error('Router event error:', error)
    });
  }

  getFeatureFlags(): void {
    const content = LIFERAY_WEB_CONSTANT.feature_flags;
    this.liferayService.getWebContent<{ featureFlagsPerEnvironment: IFeatureFlagsPerEnvironment[] }>(content.type, content.id)
      .subscribe({
        next: (featureFlags) => {
          const flagsPerEnv = featureFlags?.value?.featureFlagsPerEnvironment || null;
          // find the flags for the current environment, default to null if not found
          const flagsForCurrentEnv = flagsPerEnv?.find(
            (envFlags: IFeatureFlagsPerEnvironment) => 
              environment.env === 'prod' ? envFlags.environment === 'higher' : envFlags.environment === 'lower'
          ) || null;
          // send the flags in BS so that can be accessed by different components and services
          this.dataService.featureFlags$.next(flagsForCurrentEnv);
        },
        error: (error) => {
          console.error('Error fetching feature flags:', error);
        }
      });
  }

  ngOnDestroy(): void {
    if (this.keydownListener) {
      window.removeEventListener("keydown", this.keydownListener);
    }
    if (this.mousedownListener) {
      window.removeEventListener("mousedown", this.mousedownListener);
    }
    this.idleTracker.stopTracking();
    this.authData.cleanup();
    this.cleaner$.next();
    this.cleaner$.complete();
  }
}
